import axios from "axios";

import endpoints from "./api/endpoints";
import { setClientId, resolveClientId } from "./init";

/**
 * The tenant configs endpoint returns a map of tenantId -> config[]:
 *
 *   {
 *     [TENANT_ID]: [
 *       {
 *         id, tenant_id,
 *         configuration: {
 *           domains: [{ domain, google_tag_manager, lp_domain }]
 *         }
 *       }
 *     ]
 *   }
 *
 * We flatten the domains into the maps window.ini used to hold statically:
 *   - CLIENTS             hostname (lp_domain / domain) -> tenantId
 *   - CLIENTS_DOMAIN      tenantId -> brand domain (redirect target)
 *   - GOOGLE_TAG_MANAGERS lp_domain -> gtm id
 *
 * Whatever ini.js already defines is kept as a fallback and only overridden
 * for the hostnames/tenants present in the response.
 */
const applyTenantsConfig = (data) => {
  const clients = { ...(window.ini.CLIENTS || {}) };
  const clientsDomain = { ...(window.ini.CLIENTS_DOMAIN || {}) };
  const googleTagManagers = { ...(window.ini.GOOGLE_TAG_MANAGERS || {}) };

  Object.keys(data).forEach((tenantKey) => {
    const tenantConfigs = Array.isArray(data[tenantKey]) ? data[tenantKey] : data[tenantKey] ? [data[tenantKey]] : [];

    tenantConfigs.forEach((config) => {
      const tenantId = String(config?.tenant_id ?? tenantKey);
      const domains = config?.configuration?.domains;
      if (!Array.isArray(domains)) return;

      domains.forEach((entry) => {
        if (!entry) return;

        const domain = entry.domain;
        const lpDomain = entry.lp_domain;
        const gtm = entry.google_tag_manager;

        // The LP app is served on lp_domain, so map it (and the brand domain,
        // for robustness) to the tenant. init.js resolves clientId from these
        // using a lowercased window.location.hostname.
        if (lpDomain) clients[lpDomain.toLowerCase()] = tenantId;
        if (domain) clients[domain.toLowerCase()] = tenantId;

        // Redirect target when no matching landing page is found -> brand site.
        if (domain) clientsDomain[tenantId] = domain;

        // GTM is keyed by the LP host (matches the index.html lookup).
        if (lpDomain && gtm) googleTagManagers[lpDomain.toLowerCase()] = gtm;
      });
    });
  });

  window.ini.CLIENTS = clients;
  window.ini.CLIENTS_DOMAIN = clientsDomain;
  window.ini.GOOGLE_TAG_MANAGERS = googleTagManagers;
};

/**
 * Fetches the tenant configs and prefills window.ini before the app renders.
 * On failure it logs and continues with whatever ini.js provided so the app
 * still boots (falling back to the hardcoded maps / DEFAULT_CLIENT_ID).
 */
const bootstrapConfig = async () => {
  try {
    const { data } = await axios.get(`${endpoints.tenantsConfigsApiUrl}?domain=${window.location.hostname}`);
    if (data && typeof data === "object") {
      applyTenantsConfig(data);
    }
  } catch (e) {
    console.error("[bootstrapConfig] failed to fetch tenant configs:", e);
  }

  // Recompute clientId now that CLIENTS may have been (re)populated.
  setClientId(resolveClientId());
};

export default bootstrapConfig;
